/**
 * Token Manager - Stores access token in memory (not localStorage)
 * This prevents XSS attacks from stealing tokens
 */

let accessToken = null;
let tokenExpiry = null;

// Set when this tab's session was forcibly evicted (signed in from another
// device/screen) — see socketInboxUpdater.js's handleForcedDisconnect.
// While true, tokenRefreshService.js stops its background silent refresh and
// networkErrorHandler.js stops its "no token -> hard redirect" behavior, so
// the tab genuinely stays logged out (showing only the eviction toast) rather
// than silently re-authenticating itself via the still-valid refresh cookie.
// Cleared automatically the next time a real token is set (fresh login, or a
// user-initiated reconnect that re-authenticates), and naturally resets on a
// full page reload since this is in-memory only.
let isEvicted = false;

/**
 * Set access token in memory
 * @param {string} token - Access token
 * @param {number} expiresIn - Expiration time in seconds (default: 900 = 15 minutes)
 */
export const setAccessToken = (token, expiresIn = 900) => {
  accessToken = token;
  // Calculate expiry time (expiresIn seconds from now)
  tokenExpiry = Date.now() + (expiresIn * 1000);
  isEvicted = false;
};

/**
 * Mark/unmark this tab as evicted (see comment on the module-level flag above).
 */
export const setEvicted = (value) => {
  isEvicted = value;
};

/**
 * @returns {boolean} True if this tab's session was forcibly evicted and hasn't re-authenticated since.
 */
export const isEvictedState = () => isEvicted;

/**
 * Get access token from memory
 * @returns {string|null} Access token or null if not set/expired
 */
export const getAccessToken = () => {
  // Check if token exists and is not expired
  if (accessToken && tokenExpiry && Date.now() < tokenExpiry) {
    return accessToken;
  }
  
  // Token expired or doesn't exist
  if (accessToken && tokenExpiry && Date.now() >= tokenExpiry) {
    // Clear expired token
    clearAccessToken();
  }
  
  return null;
};

/**
 * Clear access token from memory
 */
export const clearAccessToken = () => {
  accessToken = null;
  tokenExpiry = null;
};

/**
 * Check if token is expired or will expire soon
 * @param {number} bufferSeconds - Buffer time in seconds before expiry (default: 120 = 2 minutes)
 * @returns {boolean} True if token is expired or will expire soon
 */
export const isTokenExpiredOrExpiringSoon = (bufferSeconds = 120) => {
  if (!accessToken || !tokenExpiry) {
    return true;
  }
  
  // Check if token will expire within buffer time
  return Date.now() >= (tokenExpiry - (bufferSeconds * 1000));
};

/**
 * Get time until token expires (in milliseconds)
 * @returns {number} Milliseconds until expiry, or 0 if expired/not set
 */
export const getTimeUntilExpiry = () => {
  if (!tokenExpiry) {
    return 0;
  }
  
  const timeLeft = tokenExpiry - Date.now();
  return timeLeft > 0 ? timeLeft : 0;
};

/**
 * Get token expiry time (timestamp)
 * @returns {number|null} Expiry timestamp or null if not set
 */
export const getTokenExpiryTime = () => {
  return tokenExpiry;
};

